<template>
  <div class="billing-info-wrapper">
    <dropdown
      :key="key"
      v-model="countryName"
      :editable="true"
      :options="countries"
      :auto-complete="true"
      :title="$t('country')"
      :disabled="disablecondition === 1"
      @focus-next-input="focusNext('companyInputRef')"
      @input="countrySelected"
    />
    <editable-input
      ref="companyInputRef"
      v-model="companyName"
      :title="businessPurchase ? $t('companyName') : $t('name')"
      reference="companyInputRef"
      :incorrect-value-msg="$t('fieldCannotBeEmpty')"
      :disabled="disablecondition === 1"
      lazy-validation
      disable-auto-complete
      @focus-next-input="focusNext('line1')"
    />

    <editable-input
      id="line1"
      ref="line1"
      v-model="addressLine1"
      reference="line1"
      lazy-validation
      :incorrect-value-msg="$t('fieldCannotBeEmpty')"
      :title="$t('addressLine1')"
      :disabled="disablecondition === 1"
      @focus-next-input="focusNext('line2')"
    />
    <editable-input
      id="line2"
      ref="line2"
      v-model="addressLine2"
      reference="line2"
      lazy-validation
      :placeholder="$t('phOptional')"
      :disabled="disablecondition === 1"
      @focus-next-input="focusNext('city')"
    />

    <div class="row">
      <editable-input
        id="city"
        ref="city"
        v-model="city"
        reference="city"
        lazy-validation
        :title="$t('city')"
        :incorrect-value-msg="$t('fieldCannotBeEmpty')"
        :disabled="disablecondition === 1"
        @keyup.enter="$event.target.nextElementSibling.focus()"
        @focus-next-input="focusNext('stateProvince')"
      />
      <editable-input
        id="state-province"
        ref="stateProvince"
        v-model="stateProvince"
        reference="stateProvince"
        lazy-validation
        :title="$t('stateProvince')"
        :placeholder="$t('phOptional')"
        :disabled="disablecondition === 1"
        @focus-next-input="focusNext('zipCode')"
      />
    </div>
    <editable-input
      id="zip-code"
      ref="zipCode"
      v-model="zipCode"
      reference="zipCode"
      lazy-validation
      :incorrect-value-msg="$t('fieldCannotBeEmpty')"
      :disabled="disablecondition === 1"
      :title="$t('zipCode')"
      @focus-next-input="focusNext(isEU && businessPurchase ? 'vatId' : 'nameoncard')"
    />
    <p v-if="isEU" class="radio-group-subtitle">
      {{ $t('isBusinessPurchase') }}
    </p>
    <radio-button v-if="isEU" :text="'yes'" class="radio-button" :checked="businessPurchase" @click="businessPurchase = !businessPurchase" />
    <radio-button v-if="isEU" :text="'no'" class="radio-button" :checked="!businessPurchase" @click="businessPurchase = !businessPurchase" />
    <editable-input
      v-if="isEU && businessPurchase"
      id="vat-id"
      ref="vatId"
      v-model="vatId"
      reference="vatId"
      :vat-id-string="countryCode"
      vat-restriction
      :incorrect-value-msg="vatId === '' ? $t('fieldCannotBeEmpty') : incorrectNipText"
      :disabled="disablecondition === 1"
      :placeholder="countryCode === 'PL' ? '999999999' : countryCode + '999999999'"
      :title="countryCode === 'PL' ? $t('nip') : $t('vatId')"
      :footer="countryCode === 'PL' ? '' : $t('includeYourCountryCode')"
      @focus-next-input="focusNext('nameOnCard')"
    />
    <div class="button-row">
      <button class="clavo-button primary" :class="{ 'clavo-button-inactive': disablecondition }" :disabled="disablecondition === 1" @click="saveButtonClicked">
        {{ $t('save') }}
      </button>
      <button class="clavo-button secondary" :class="{ 'clavo-button-inactive': disablecondition }" :disabled="disablecondition === 1" @click="$emit('close')">
        {{ $t('cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
import ParseFunctions from '@/components/ParseFunctions'
import EditableInput from '@/components/UI/EditableInput'
import RadioButton from '@/components/UI/RadioButton'
import Dropdown from '@/components/UI/Dropdown'
import Validation from '@/components/Validation'
import BillingUtils from '@/components/Billing/BillingUtils'

export default {
  name: 'BillingInformation',
  components: {
    EditableInput,
    RadioButton,
    Dropdown,
  },
  mixins: [ParseFunctions, Validation, BillingUtils],
  props: {
    stateProvinceProp: String,
    companyNameProp: String,
    addressLine1Prop: String,
    addressLine2Prop: String,
    zipCodeProp: String,
    cityProp: String,
    countryCodeProp: String,
    businessPurchaseProp: Boolean,
    vatIdProp: String,
    teamId: String,
  },
  data() {
    return {
      countryName: '',
      countries: [],
      vatRates: [],
      companyName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      plan: '',
      vatId: '',
      stateProvince: '',
      businessPurchase: true,
      countryCode: 'PL',
      businessPurchaseToggled: true,
      disablecondition: false,
      key: '6',
      incorrectNipText: this.$t('fieldCannotBeEmpty'),
    }
  },
  computed: {
    isEU() {
      for (let o of this.vatRates) {
        if (o.name === this.countryName) return true
      }
      return false
    },
  },
  async created() {
    try {
      let res
      this.stateProvince = this.stateProvinceProp
      this.companyName = this.companyNameProp
      this.addressLine1 = this.addressLine1Prop
      this.addressLine2 = this.addressLine2Prop
      this.zipCode = this.zipCodeProp
      this.city = this.cityProp
      this.countryCode = this.countryCodeProp
      this.businessPurchase = this.businessPurchaseProp
      if (this.businessPurchase) {
        this.vatId = this.vatIdProp
      }
      res = await this.getCountries()
      if (res.status === 'ok') {
        for (let key in res.countries) {
          this.countries.push({ code: key, name: res.countries[key].name })
        }
      }
      ;(this.key = '9'), (this.countryName = this.getNameFromCode(this.countryCode))

      res = await this.getVatRates()
      if (res.status === 'ok') {
        for (let key in res.taxRates) {
          let name = this.getNameFromCode(key)
          this.vatRates.push({ code: key, rate: res.taxRates[key], name: name })
        }
      }
    } catch (e) {
      // console.log(e)
    }
  },
  async mounted() {},
  methods: {
    async countrySelected() {
      // this.countryCode = this.country.code
      this.countryCode = this.getCodeFromName(this.countryName)
      // if(!this.cardLast4 )
      //   await this.focusNext("companyInputRef")
    },

    handlePaymentError(errorResp) {
      if (errorResp) {
        this.disablecondition = 0
        // this.cardNumber.update({ disabled : false })
        // this.cardExpiry.update({ disabled : false })
        // this.cardCvc.update({ disabled : false })
        this.paybuttontext = this.$t('paynow')
        this.showPaymentError(errorResp)
        this.clicked = false
      }
    },
    focusNext(dst) {
      this.$refs[dst].focusIn()
    },
    getNameFromCode(code) {
      for (let o of this.countries) {
        if (o.code === code) return o.name
      }
    },
    getCodeFromName(name) {
      for (let o of this.countries) {
        if (o.name === name) return o.code
      }
    },
    checkIfCorrect() {
      let correct = true
      if (!this.cardLast4) {
        correct = this.$refs.companyInputRef.validate()
        correct = this.$refs.line1.validate() && correct
        correct = this.$refs.city.validate() && correct
        correct = this.$refs.zipCode.validate() && correct
        if (this.businessPurchase && this.isEU) correct = this.$refs.vatId.validate() && correct
      }
      return correct
    },
    async saveButtonClicked() {
      if (this.checkIfCorrect()) {
        this.disablecondition = 1
        let res
        if (this.isEU) {
          if (this.businessPurchase) {
            res = await this.updateSubscriptionDetailsEU(
              this.companyName,
              this.countryCode,
              this.businessPurchase,
              this.teamId,
              this.addressLine1,
              this.addressLine2,
              this.city,
              this.stateProvince,
              this.zipCode,
              this.vatId,
            )
          } else {
            res = await this.updateSubscriptionDetailsEU(
              this.companyName,
              this.countryCode,
              this.businessPurchase,
              this.teamId,
              this.addressLine1,
              this.addressLine2,
              this.city,
              this.stateProvince,
              this.zipCode,
            )
          }
        } else {
          res = await this.updateSubscriptionDetailsNonEU(
            this.companyName,
            this.countryCode,
            this.businessPurchase,
            this.teamId,
            this.addressLine1,
            this.addressLine2,
            this.city,
            this.stateProvince,
            this.zipCode,
          )
        }
        // console.log('res', res)
        if (res.status === 'ok') {
          this.disablecondition = 0
          this.$emit('save', this.businessPurchase)
        } else {
          this.showPaymentError(res)
          this.disablecondition = 0
        }
      } else {
        // console.log('failed')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.billing-info-wrapper {
  grid-column: 1 / span 2;
  > div {
    margin-bottom: 24px;
    flex-grow: 1;
  }

  p {
    margin-bottom: 24px;
    margin-top: 48px;
  }
  .button-row {
    margin-top: 48px;
    button {
      flex: 1;
      &:first-of-type {
        margin-right: 20px;
      }
    }

    @include respond-below(xs) {
      display: flex;
      flex-wrap: wrap;
      button {
        min-width: 100px;
      }
    }
  }
  .radio-button {
    margin-bottom: 0px;
  }

  #line2 {
    margin-top: -12px;
  }
  #vat-id {
    margin-top: 24px;
  }

  .row {
    //flex-grow: 1;
    margin-bottom: 24px;
    display: flex;
    flex-direction: row;

    > div {
      flex-grow: 1;
    }

    // display: grid;
    // grid-template-columns: 245px 245px;
    // flex-direction: row;
    // grid-column-gap: 10px;
    // grid-auto-rows: auto;
    // grid-row-gap: 30px;

    div:first-of-type {
      margin-right: 20px;
      @include respond-below(xs) {
        margin-right: 0px;
        margin-bottom: 24px;
      }
    }

    @include respond-below(xs) {
      flex-direction: column;
    }
  }
}
</style>
